import { Avatar, Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Ticket, TicketDetails, typeResponse } from "../../../../utils/types";
import { AccessTime, AdminPanelSettings, AttachFile, Close, Person, Send, Delete, CloudUpload, InsertDriveFile } from "@mui/icons-material";
import { formatDate, getSupportCategoryItemById } from "../../../../utils/helpers";
import { useEffect, useState } from "react";
import instance from "../../../../providers/axios.config";
import Notiflix from "notiflix";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/reducers/rootReducer";

interface SupportTicketDetailsProps {
  selectedTicket: TicketDetails;
  open: boolean;
  onClose: () => void;
  showFeedback?: boolean;
  autoFeedbackRating?: number | null;
  onFeedbackSubmit?: () => void;
}

const SupportTicketDetails = ({
  selectedTicket,
  open,
  onClose,
  showFeedback = false,
  autoFeedbackRating = null,
  onFeedbackSubmit
}: SupportTicketDetailsProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);
  const [newMessage, setNewMessage] = useState<string>('');
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const account = useSelector((state: RootState) => state.user.usersData);

  // Message validation
  const minCharacters = 10;
  const maxCharacters = 5000;
  const characterCount = newMessage.length;
  const isMessageValid = characterCount >= minCharacters && characterCount <= maxCharacters;

  useEffect(() => {
    if (autoFeedbackRating && selectedTicket && selectedTicket.status === 'closed' && !selectedTicket.rating) {
      handleFeedbackSubmit(autoFeedbackRating);
    }
  }, [autoFeedbackRating, selectedTicket]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'primary';
      case 'in_progress': return 'warning';
      // case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  // Feedback submission handler
  const handleFeedbackSubmit = async (rating: number) => {
    try {
      setSubmittingFeedback(true);
      const resp = await instance.post(`support/tickets/${selectedTicket.id}/feedback`, { 
        rating 
      }) as typeResponse;

      if ((resp as any).status === 'success') {
        Notiflix.Notify.success(t('feedback_submitted_successfully'));
        
        // Call the callback to refresh data and hide feedback section
        if (onFeedbackSubmit) {
          onFeedbackSubmit();
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      Notiflix.Notify.failure(t('error_submitting_feedback'));
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // File handling functions
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleReplyFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleReplyFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        Notiflix.Notify.warning(t('file_too_large', { name: file.name }));
        return false;
      }

      // Check file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'application/zip',
        'application/x-zip-compressed'
      ];

      if (!allowedTypes.includes(file.type)) {
        Notiflix.Notify.warning(t('file_type_not_allowed', { name: file.name }));
        return false;
      }

      return true;
    });

    // Check total number of files
    if (replyAttachments.length + validFiles.length > 5) {
      Notiflix.Notify.warning(t('max_files_exceeded'));
      return;
    }

    setReplyAttachments(prev => [...prev, ...validFiles]);
  };

  const removeReplyFile = (index: number) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSendReply = async () => {
    if (!selectedTicket || !newMessage.trim() || !isMessageValid) return;

    try {
      setLoading(true);

      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('message', newMessage);

      // Add attachments
      replyAttachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      const resp = await instance.post(`support/tickets/${selectedTicket.id}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }) as typeResponse;

      if ((resp as any).status === 'success') {
        setNewMessage('');
        setReplyAttachments([]);
        Notiflix.Notify.success(t('message_sent_successfully'));
        // You might want to refresh the ticket data here
        onClose(); // Close dialog to refresh data
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      Notiflix.Notify.failure(t('error_sending_message'));
    } finally {
      setLoading(false);
    }
  };

  const getCharacterCountColor = () => {
    if (characterCount < minCharacters) return 'error.main';
    if (characterCount > maxCharacters * 0.9) return 'warning.main';
    return 'text.secondary';
  };

  const handleAttachmentDownload = async (attachmentId: string, filename: string) => {
    try {
      // Show loading state
      const loadingElement = document.querySelector(`[data-attachment="${attachmentId}"]`);
      if (loadingElement) {
        loadingElement.textContent = t('downloading') + '...';
      }

      const response: any = await instance.get(`support/attachments/${attachmentId}/download`);

      if (response.status === 'success') {
        if (response.data.download_url) {
          // Create a temporary link and trigger download
          const link = document.createElement('a');
          link.href = response.data.download_url;
          link.download = response.data.filename || filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          Notiflix.Notify.success(t('download_attachment_succeeded'));
        } else {
          // Fallback for direct file downloads
          window.open(`${instance.defaults.baseURL}/support/attachments/${attachmentId}/download`, '_blank');
        }
      } else {
        Notiflix.Notify.failure(response.message || t('download_attachment_failed'));
      }
    } catch (error) {
      console.error('Download error:', error);
      Notiflix.Notify.failure(t('download_attachment_failed'));
    } finally {
      // Reset loading state
      const loadingElement = document.querySelector(`[data-attachment="${attachmentId}"]`);
      if (loadingElement) {
        loadingElement.textContent = filename;
      }
    }
  };


  const ticketSubject = getSupportCategoryItemById(selectedTicket.subject, account?.role.slug || '');
  
  return (
    <>
      {/* Ticket Details Dialog */}
      <Dialog
        open={open}
        onClose={() => onClose()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="h6">
              {t('ticket_details')} - #{selectedTicket?.ticket_number}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              {selectedTicket && (
                <>
                  <Chip
                    label={t(`status_${selectedTicket.status}`)}
                    size="small"
                    color={getStatusColor(selectedTicket.status) as any}
                  />
                </>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => onClose()}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {selectedTicket && (
            <>
              {/* Feedback Section - Show at the top if conditions are met */}
              {showFeedback && selectedTicket.status === 'closed' && !selectedTicket.rating && (
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    m: 2,
                    backgroundColor: '#fff9e6',
                    border: '1px solid #ffc107',
                    borderLeft: '4px solid #ffc107'
                  }}
                >
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      textAlign: 'center', 
                      fontWeight: 600, 
                      color: '#1a1a1a',
                      mb: 1 
                    }}
                  >
                    {t('was_support_experience_helpful')}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ textAlign: 'center', mb: 3 }}
                  >
                    {t('feedback_helps_improve')}
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: 1, 
                    flexWrap: 'wrap' 
                  }}>
                    <Button
                      variant="contained"
                      onClick={() => handleFeedbackSubmit(5)}
                      disabled={submittingFeedback}
                      sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        '&:hover': { backgroundColor: '#388e3c' },
                        minWidth: { xs: '100%', sm: '110px' },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: 1.5
                      }}
                    >
                      {t('rating_excellent')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleFeedbackSubmit(4)}
                      disabled={submittingFeedback}
                      sx={{
                        backgroundColor: '#8bc34a',
                        color: 'white',
                        '&:hover': { backgroundColor: '#689f38' },
                        minWidth: { xs: '100%', sm: '110px' },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: 1.5
                      }}
                    >
                      {t('rating_good')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleFeedbackSubmit(3)}
                      disabled={submittingFeedback}
                      sx={{
                        backgroundColor: '#ffc107',
                        color: '#000',
                        '&:hover': { backgroundColor: '#ffa000' },
                        minWidth: { xs: '100%', sm: '110px' },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: 1.5
                      }}
                    >
                      {t('rating_average')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleFeedbackSubmit(2)}
                      disabled={submittingFeedback}
                      sx={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        '&:hover': { backgroundColor: '#f57c00' },
                        minWidth: { xs: '100%', sm: '110px' },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: 1.5
                      }}
                    >
                      {t('rating_poor')}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleFeedbackSubmit(1)}
                      disabled={submittingFeedback}
                      sx={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        '&:hover': { backgroundColor: '#d32f2f' },
                        minWidth: { xs: '100%', sm: '110px' },
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        py: 1.5
                      }}
                    >
                      {t('rating_very_poor')}
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Ticket Info */}
              <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('subject')}</Typography>
                    {ticketSubject && <Typography variant="body1">{t(ticketSubject.name)}</Typography>}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('created')}</Typography>
                    <Typography variant="body1">{formatDate(selectedTicket.created_at, true)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">{t('last_updated')}</Typography>
                    <Typography variant="body1">{formatDate(selectedTicket.updated_at, true)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Messages */}
              <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 2 }}>
                <Stack spacing={2}>
                  {selectedTicket.messages.map((msg, index) => {
                    return (
                      <Box key={msg.id}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                          flexDirection: msg.sender_type === 'user' ? 'row-reverse' : 'row'
                        }}>
                          <Avatar sx={{
                            bgcolor: msg.sender_type === 'admin' ? 'primary.main' : 'grey.400',
                            width: 32,
                            height: 32
                          }}>
                            {msg.sender_type === 'admin' ? <AdminPanelSettings /> : <Person />}
                          </Avatar>

                          <Box sx={{
                            maxWidth: '70%',
                            backgroundColor: msg.sender_type === 'user' ? 'primary.light' : 'grey.100',
                            borderRadius: 2,
                            p: 1.5
                          }}>
                            <Box sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1
                            }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: msg.sender_type === 'user' ? '#fff' : '' }}>
                                {msg.sender_name}
                              </Typography>
                              <Typography variant="caption" color={`${msg.sender_type === 'user' ? '#fff' : 'text.secondary'}`}>
                                <AccessTime sx={{ fontSize: 12, mr: 0.5 }} />
                                {formatDate(msg.created_at, true)}
                              </Typography>
                            </Box>

                            <Typography fontWeight={300} variant="body2" color={`${msg.sender_type === 'user' ? '#fff' : '#000'}`}>
                              {msg.message}
                            </Typography>

                            {msg.attachments && msg.attachments.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {msg.attachments.map((attachment, idx) => (
                                  <Chip
                                    key={idx}
                                    icon={<AttachFile />}
                                    label={attachment.file_name}
                                    size="small"
                                    color={'success'}
                                    clickable
                                    onClick={() => handleAttachmentDownload(attachment.id, attachment.file_name)}
                                    sx={{ mr: 1, mb: 1 }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {index < selectedTicket.messages.length - 1 && <Divider sx={{ my: 1 }} />}
                      </Box>
                    )
                  })}
                </Stack>
              </Box>

              {/* Reply Section */}
              {selectedTicket.assigned_to && selectedTicket.status !== 'closed' && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={t('type_your_reply')}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    variant="outlined"
                    size="small"
                    error={characterCount > 0 && !isMessageValid}
                    sx={{ fontSize: 12 }}
                  />

                  {/* Character Counter */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: getCharacterCountColor() }}>
                      {characterCount}/{maxCharacters} {t('characters')}
                      {characterCount < minCharacters && (
                        <span> • {t('minimum_required')}: {minCharacters}</span>
                      )}
                    </Typography>
                  </Box>

                  {/* File Attachment Section */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      {t('attach_files')} ({t('optional')})
                    </Typography>

                    {/* Compact Drag and Drop Area */}
                    <Box
                      sx={{
                        border: `1px dashed ${dragActive ? 'primary.main' : 'grey.300'}`,
                        borderRadius: 1,
                        p: 2,
                        textAlign: 'center',
                        bgcolor: dragActive ? 'action.hover' : 'grey.50',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover'
                        }
                      }}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('reply-file-input')?.click()}
                    >
                      <input
                        id="reply-file-input"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip"
                        onChange={(e) => e.target.files && handleReplyFiles(Array.from(e.target.files))}
                        style={{ display: 'none' }}
                      />

                      <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'} justifyContent={'center'}>
                        <CloudUpload sx={{ fontSize: 24, color: 'grey.400', mr: 1 }} />
                        <Typography variant="body2" display="inline">
                          {t('drag_drop_files_here')} ({replyAttachments.length}/5)
                        </Typography>
                      </Box>
                      <Typography variant="subtitle2" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        {t('max_file_size_10mb')} • {t('max_5_files')}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary" display="block">
                        {t('allowed_formats')}: PDF, DOC, DOCX, TXT, JPG, PNG, ZIP
                      </Typography>
                    </Box>

                    {/* Attached Files List */}
                    {replyAttachments.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {replyAttachments.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1,
                              mb: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                              borderRadius: 1,
                              bgcolor: 'background.paper'
                            }}
                          >
                            <InsertDriveFile sx={{ mr: 1, color: 'primary.main', fontSize: 16 }} />
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography variant="caption" noWrap>
                                {file.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {formatFileSize(file.size)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => removeReplyFile(index)}
                              sx={{ color: 'error.main' }}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<Send />}
                      onClick={handleSendReply}
                      disabled={!isMessageValid || loading}
                      size="small"
                    >
                      {loading ? t('sending') : t('send_reply')}
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog >
    </>
  );
};

export default SupportTicketDetails;